import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';

import { IconSearchLine } from '../Icon';
import { Input } from '../Input';
import { INPUT_HEIGHT_BY_SIZE } from '../Input/Input.css';
import {
  DropdownRoot,
  DropdownTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownProps,
} from './Dropdown';
import { DROPDOWN_MENU_MAX_HEIGHT } from './Dropdown.css';
import type { DropdownItemDef, DropdownMenuItemType } from './types';

export interface ComboBoxProps extends Omit<DropdownProps, 'menu'> {
  children: React.ReactElement;
  value: string | string[] | undefined;
  items: DropdownMenuItemType[];
  onChange?: (value: string, item: DropdownItemDef) => void;
  searchPlaceholder?: string;
  disabled?: boolean;
  loading?: boolean;
  onSearch?: (searchTerm: string) => void;
  emptyMessage?: string;
  /**
   * 필터링 함수. 서버 사이드 필터링을 사용할 경우 `null`로 설정하면 클라이언트 필터링이 비활성화됩니다.
   * @default defaultFilterItems (클라이언트 사이드 필터링)
   */
  filterItems?:
    | ((items: DropdownMenuItemType[], searchTerm: string) => DropdownMenuItemType[])
    | null;
  inputSize?: 's' | 'm' | 'l';
  /**
   * 타이핑 감지 활성화. Cell Editor 등에서 사용 시 true로 설정하면
   * 키보드 입력을 감지하여 자동으로 드롭다운을 열고 검색어를 설정합니다.
   */
  enableTypingDetection?: boolean;
  /**
   * 드롭다운 메뉴 하단에 표시할 컨텐츠
   */
  bottomContent?: React.ReactNode;
  /**
   * 스크롤 가상화 활성화 여부 (많은 항목을 렌더링할 때 사용)
   * - true: 기본 옵션
   * - { options }: 가상화 옵션 지정
   */
  virtualized?: boolean;
  maxHeight?: number;
}

const DROPDOWN_MENU_VERTICAL_PADDING = 12;
const SEARCH_CONTAINER_VERTICAL_PADDING = 18;

const defaultFilterItems = (
  items: DropdownMenuItemType[],
  searchTerm: string,
): DropdownMenuItemType[] => {
  if (!searchTerm) return items;

  const lowerSearchTerm = searchTerm.toLowerCase();
  return items.filter((item) => {
    if (item.type === 'divider') return false;
    if (typeof item.label === 'string') {
      return item.label.toLowerCase().includes(lowerSearchTerm);
    }
    return true;
  });
};

export const ComboBox: React.FC<ComboBoxProps> = ({
  children,
  value,
  items,
  onChange,
  searchPlaceholder = '검색...',
  disabled = false,
  loading = false,
  onSearch,
  filterItems = defaultFilterItems,
  emptyMessage = '검색 결과가 없습니다',
  inputSize = 'm',
  width,
  menuOffset = 4,
  preventScroll,
  zIndex,
  selectable,
  open,
  onOpenChange,
  enableTypingDetection = false,
  bottomContent,
  virtualized = false,
  maxHeight = DROPDOWN_MENU_MAX_HEIGHT,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // controlled/uncontrolled 패턴
  const isOpen = useMemo(() => open ?? internalOpen, [open, internalOpen]);
  const setIsOpen = useMemo(() => onOpenChange ?? setInternalOpen, [onOpenChange, setInternalOpen]);
  const inputRef = useRef<HTMLInputElement>(null);

  // 필터링된 아이템들
  // filterItems가 null이면 필터링하지 않음 (서버 사이드 필터링 사용 시)
  const filteredItems = useMemo(() => {
    if (filterItems === null) {
      return items;
    }
    return filterItems(items, searchTerm);
  }, [items, searchTerm, filterItems]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    onSearch?.(newSearchTerm);
  };

  const handleItemClick = useCallback(
    (
      _e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
      item: DropdownItemDef,
    ) => {
      onChange?.(item.key, item);
    },
    [onChange],
  );

  const selectedKeys = useMemo(
    () => (value ? (Array.isArray(value) ? value : [value]) : undefined),
    [value],
  );

  const defaultVirtualizedMaxHeight =
    maxHeight -
    DROPDOWN_MENU_VERTICAL_PADDING -
    (INPUT_HEIGHT_BY_SIZE[inputSize] + SEARCH_CONTAINER_VERTICAL_PADDING);

  const resolvedVirtualized = virtualized
    ? {
        options: {
          maxHeight: defaultVirtualizedMaxHeight,
        },
      }
    : false;

  // 드롭다운 열릴 때 Input에 포커스
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    } else {
      setSearchTerm('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!enableTypingDetection || disabled || isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // 일반 문자(타이핑 가능한 문자)를 정규식으로 체크
      const isTypingChar = /^[\w\s가-힣ㄱ-ㅎㅏ-ㅣ!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]$/.test(
        e.key,
      );

      if (isTypingChar && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        e.stopPropagation();

        setSearchTerm(e.key);
        onSearch?.(e.key);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [disabled, enableTypingDetection, isOpen, onSearch]);

  return (
    <DropdownRoot
      open={isOpen}
      onOpenChange={setIsOpen}
      menuOffset={menuOffset}
      itemCount={filteredItems.length}
      disableItemFocus
      width={width}
    >
      <DropdownTrigger disabled={disabled}>{children}</DropdownTrigger>
      <DropdownMenu preventScroll={preventScroll} zIndex={zIndex}>
        <div style={{ padding: '6px 6px 12px 6px' }}>
          <Input
            ref={inputRef}
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={searchPlaceholder}
            disabled={disabled}
            loading={loading}
            startIcon={<IconSearchLine width={20} height={20} />}
            inputSize={inputSize}
            width="100%"
          />
        </div>
        {filteredItems.length === 0 ? (
          <DropdownMenuItem disabled>{emptyMessage}</DropdownMenuItem>
        ) : (
          <DropdownMenuContent
            items={filteredItems}
            onItemClick={handleItemClick}
            selectedKeys={selectedKeys}
            selectable={selectable}
            virtualized={resolvedVirtualized}
          />
        )}
        {bottomContent ? bottomContent : null}
      </DropdownMenu>
    </DropdownRoot>
  );
};
